import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  StatusBar,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import {
  Button,
  Input,
  PhoneInput,
  DualPaneLayout,
  SocialLogin,
  Logo,
  colors,
  spacing,
  typography,
} from '../components';
import { useAppContext } from '../contexts/AppContext';
import { useAppStore } from '../store/appStore';
import { apiService } from '../services/api';
import { handleApiError, logError } from '../utils/errorHandler';
import { useLocale } from '../contexts/LocaleContext';
import { useTypography } from '../hooks/useTypography';
import { useThemedStyles } from '../hooks/useThemedStyles';

type LoginMode = 'phone' | 'email';

export const UnifiedLoginScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { showNotification, navigateTo } = useAppContext();
  const { setPhase1Token, setUserFromPhase1Token, isAuthenticated, tokens, phase1Token, currentBusiness, hasBusiness } = useAppStore();
  const { t, isRTL } = useLocale();
  const typo = useTypography();

  const [mode, setMode] = useState<LoginMode>('phone');
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  const phoneInputRef = useRef<{ getValue: () => { nationalNumber: string; e164Format?: string }; getCountryCode: () => string }>(null);

  // One canonical form (E.164, e.g. +966501234567) whatever the user typed
  // — "0501…" vs "501…" used to become two different mobile strings, i.e.
  // two different accounts for the same person.
  const getFullMobileNumber = (): string => {
    const phoneData: any = phoneInputRef.current?.getValue();
    if (phoneData?.nationalNumber) {
      if (phoneData.e164Format && String(phoneData.e164Format).startsWith('+')) return phoneData.e164Format;
      const countryCode = phoneInputRef.current?.getCountryCode();
      if (countryCode) return `+${countryCode}${String(phoneData.nationalNumber).replace(/^0+/, '')}`;
    }
    const raw = mobile.trim().replace(/[^\d+]/g, '');
    return raw ? (raw.startsWith('+') ? raw : `+${raw}`) : '';
  };

  const routeToMembershipsOrWizard = async () => {
    try {
      // A user with zero ACTIVE memberships may still have a pending invitation (e.g. invited
      // by email, then signing in for the first time via Google/etc.) — getMemberships()
      // deliberately excludes those (userStatus: 'pending'), so it alone can't decide "this
      // user has nothing" without also checking getPendingInvitations(); otherwise an invited
      // user gets sent straight to business-creation-wizard, skipping the picker where their
      // invitation would actually appear.
      const [membershipsResponse, invitationsResponse] = await Promise.all([
        apiService.getMemberships(),
        apiService.getPendingInvitations(),
      ]);
      const memberships = membershipsResponse.data || [];
      const invitations = invitationsResponse.data || [];
      if (memberships.length === 0 && invitations.length === 0) {
        navigateTo('business-creation-wizard');
      } else {
        navigateTo('business-picker');
      }
    } catch (err) {
      logError(err, 'UnifiedLoginScreen');
      // If memberships can't be fetched, business-picker itself will retry/handle the error
      navigateTo('business-picker');
    }
  };

  // If the user already has a valid session, don't show the login form at all — go straight
  // to home (phase-2 token with a resolved business) or the business picker (authenticated but
  // no business selected yet, e.g. app was closed mid-picker or a phase-1-only session).
  //
  // The persisted store (trusted-device tokens included) rehydrates ASYNCHRONOUSLY, typically
  // a beat after this screen has already mounted. Deciding "no session" before hydration
  // finishes was the bug — this waited on Zustand's persist.hasHydrated()/onFinishHydration
  // (the store's actual "rehydration is done" signal) instead of trusting whatever the store
  // happens to hold on the very first render.
  useEffect(() => {
    let cancelled = false;

    const checkExistingSession = async () => {
      const hasPhase2Session = isAuthenticated && !!tokens?.token;
      const hasPhase1Session = !hasPhase2Session && !!phase1Token;

      if (!hasPhase2Session && !hasPhase1Session) {
        if (!cancelled) setIsCheckingSession(false);
        return;
      }

      if (hasPhase2Session && currentBusiness && hasBusiness) {
        navigateTo('home');
        if (!cancelled) setIsCheckingSession(false);
        return;
      }

      // Authenticated but no business resolved yet (phase-1 only, or phase-2 token that
      // somehow lost its business context) — ask the backend which state applies.
      try {
        // Same rule as routeToMembershipsOrWizard — a pending invitation must also send the
        // user to business-picker, not business-creation-wizard, even with zero active memberships.
        const [membershipsResponse, invitationsResponse] = await Promise.all([
          apiService.getMemberships(),
          apiService.getPendingInvitations(),
        ]);
        const memberships = membershipsResponse.data || [];
        const invitations = invitationsResponse.data || [];
        if (cancelled) return;
        navigateTo((memberships.length === 0 && invitations.length === 0) ? 'business-creation-wizard' : 'business-picker');
      } catch (err) {
        logError(err, 'UnifiedLoginScreen:checkExistingSession');
      } finally {
        if (!cancelled) setIsCheckingSession(false);
      }
    };

    const runOnceHydrated = () => {
      if (cancelled) return;
      checkExistingSession();
    };

    if (useAppStore.persist.hasHydrated()) {
      runOnceHydrated();
    } else {
      const unsubscribe = useAppStore.persist.onFinishHydration(runOnceHydrated);
      // Safety net: if hydration somehow never fires (e.g. storage error swallowed upstream),
      // don't leave the user stuck on the spinner forever — fall back to whatever the store
      // holds right now after a short grace period.
      const fallbackTimer = setTimeout(runOnceHydrated, 1500);
      return () => {
        cancelled = true;
        unsubscribe();
        clearTimeout(fallbackTimer);
      };
    }

    return () => { cancelled = true; };
  }, [isAuthenticated, tokens?.token, phase1Token, currentBusiness, hasBusiness, navigateTo]);

  const onSendPhoneOtp = async () => {
    const fullMobile = getFullMobileNumber();
    if (!fullMobile) {
      setError(t('validation.required'));
      return;
    }
    setError(undefined);
    setIsLoading(true);
    try {
      const response = await apiService.sendPhoneOTP(fullMobile);
      if (response.statusCode === 1000) {
        navigateTo('otp-verification', undefined, { verificationType: 'phone-whatsapp', mobile: fullMobile });
      } else {
        showNotification('error', 'Could not send code', response.errors?.[0]?.message || 'Please try again.');
      }
    } catch (err) {
      const { title, message, type } = handleApiError(err);
      showNotification(type, title, message);
    } finally {
      setIsLoading(false);
    }
  };

  const onSendEmailOtp = async () => {
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
      setError(t('validation.emailInvalid'));
      return;
    }
    setError(undefined);
    setIsLoading(true);
    try {
      const response = await apiService.sendEmailLoginOTP(email.trim());
      if (response.statusCode === 1000) {
        navigateTo('otp-verification', undefined, { verificationType: 'email-login', email: email.trim() });
      } else {
        showNotification('error', 'Could not send code', response.errors?.[0]?.message || 'Please try again.');
      }
    } catch (err) {
      const { title, message, type } = handleApiError(err);
      showNotification(type, title, message);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = () => {
    if (mode === 'phone') {
      onSendPhoneOtp();
    } else {
      onSendEmailOtp();
    }
  };

  const handlePhase1Login = async (fn: () => Promise<{ token: string; expiresIn: number; isNewUser: boolean } | null>) => {
    setIsLoading(true);
    try {
      const result = await fn();
      if (!result) {
        showNotification('error', t('auth.socialLoginFailedTitle'), t('auth.socialLoginFailedBody'));
        return;
      }
      if (result.token) {
        setPhase1Token(result.token);
        setUserFromPhase1Token(result.token);
        apiService.setAuthToken(result.token);
        await routeToMembershipsOrWizard();
      }
    } catch (err) {
      const { title, message, type } = handleApiError(err);
      showNotification(type, title, message);
    } finally {
      setIsLoading(false);
    }
  };

  const onGoogleLogin = async () => {
    try {
      const { signInWithGoogle } = await import('../services/google-auth.service');
      await handlePhase1Login(async () => {
        const idToken = await signInWithGoogle();
        if (!idToken) return { token: '', expiresIn: 0, isNewUser: false }; // user cancelled — not an error
        const response = await apiService.socialLoginGoogle(idToken);
        return response.statusCode === 1000 ? response.data : null;
      });
    } catch (err) {
      const { title, message, type } = handleApiError(err);
      showNotification(type, title, message);
    }
  };

  const onMetaLogin = async () => {
    try {
      const { signInWithFacebook } = await import('../services/facebook-auth.service');
      await handlePhase1Login(async () => {
        const result = await signInWithFacebook();
        if (!result) return { token: '', expiresIn: 0, isNewUser: false }; // user cancelled — not an error
        const response = await apiService.socialLoginMeta(result.code, result.redirectUri);
        return response.statusCode === 1000 ? response.data : null;
      });
    } catch (err) {
      const { title, message, type } = handleApiError(err);
      showNotification(type, title, message);
    }
  };

  if (isCheckingSession) {
    return (
      <DualPaneLayout title="DISLIX" subtitle={t('auth.signIn')} showMobileLogo={false}>
        <SafeAreaView style={[styles.container, styles.centered]}>
          <StatusBar barStyle="dark-content" backgroundColor={colors.white} />
          <ActivityIndicator size="large" color={colors.primary} />
        </SafeAreaView>
      </DualPaneLayout>
    );
  }

  return (
    <DualPaneLayout title="DISLIX" subtitle={t('auth.signIn')} showMobileLogo={false}>
      <SafeAreaView style={styles.container}>
        <StatusBar barStyle="dark-content" backgroundColor={colors.white} />

        <KeyboardAvoidingView
          style={styles.keyboardAvoidingView}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
        >
          <ScrollView
            contentContainerStyle={styles.content}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            <View style={styles.header}>
              <Logo size="medium" />
            </View>

            <View style={styles.form}>
              <View style={[styles.modeToggle, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                <TouchableOpacity
                  style={[styles.modeButton, mode === 'phone' && styles.modeButtonActive]}
                  onPress={() => { setMode('phone'); setError(undefined); }}
                  disabled={isLoading}
                >
                  <Text style={[styles.modeButtonText, { fontFamily: typo.fontFamily.medium }, mode === 'phone' && styles.modeButtonTextActive]}>
                    {t('auth.phoneNumber')}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.modeButton, mode === 'email' && styles.modeButtonActive]}
                  onPress={() => { setMode('email'); setError(undefined); }}
                  disabled={isLoading}
                >
                  <Text style={[styles.modeButtonText, { fontFamily: typo.fontFamily.medium }, mode === 'email' && styles.modeButtonTextActive]}>
                    {t('auth.email')}
                  </Text>
                </TouchableOpacity>
              </View>

              {mode === 'phone' ? (
                <PhoneInput
                  ref={phoneInputRef as any}
                  label={t('auth.phoneNumber')}
                  value={mobile}
                  onChangeText={setMobile}
                  error={error}
                  disabled={isLoading}
                  variant="outlined"
                />
              ) : (
                <Input
                  label={t('auth.email')}
                  placeholder={t('auth.emailPlaceholder')}
                  value={email}
                  onChangeText={setEmail}
                  autoCapitalize="none"
                  keyboardType="email-address"
                  autoComplete="email"
                  error={error}
                  editable={!isLoading}
                  variant="outlined"
                  latin
                />
              )}

              <View style={styles.actions}>
                <Button
                  title={t('auth.continue')}
                  onPress={onSubmit}
                  variant="primary"
                  size="xl"
                  fullWidth
                  loading={isLoading}
                  style={styles.submitButton}
                />
              </View>

              <SocialLogin
                onGoogleLogin={onGoogleLogin}
                onFacebookLogin={onMetaLogin}
                isLoading={isLoading}
              />
            </View>
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </DualPaneLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.surface,
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  centered: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: spacing.xl,
    paddingBottom: spacing.xl,
  },
  header: {
    alignItems: 'center',
    marginBottom: spacing.xl * 4,
  },
  form: {
    width: '100%',
    maxWidth: 480,
    alignSelf: 'center',
    paddingHorizontal: spacing.screenPadding,
  },
  modeToggle: {
    marginBottom: spacing.lg,
    backgroundColor: colors.lightGray,
    borderRadius: spacing.borderRadius.lg,
    padding: 4,
  },
  modeButton: {
    flex: 1,
    paddingVertical: spacing.sm,
    alignItems: 'center',
    borderRadius: spacing.borderRadius.md,
  },
  // Active pill uses the theme's own surface color (not a hardcoded white) so the selected
  // tab's text — themed via modeButtonTextActive below — always has real contrast against it,
  // in both light and dark mode.
  modeButtonActive: {
    backgroundColor: colors.background.surface,
  },
  modeButtonText: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
  },
  modeButtonTextActive: {
    color: colors.primary,
    fontWeight: '700',
  },
  actions: {
    marginTop: spacing.lg,
  },
  submitButton: {},
});
